// Core
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, Crumbs, Segmented } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { openNotificationPopup } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useAddNewMemberMutation } from "state/members/membersApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  nif: "",
  address: "",
  status: "ACTIVE",
  is_host: false,
  is_promoter: false,
  has_dashboard_access: false,
};

/* Single-page member creation, built with the same rec-* wizard primitives as
   the resource creation (NewResource) so it matches the f-core-revamped design:
   a live preview hero, the "Informação Geral" fields, a status segmented control
   and role toggle cards. */
function NewMember() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["members", "common"]);

  const [form, setForm] = useState(EMPTY);
  const [nameErr, setNameErr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addNewMember] = useAddNewMemberMutation();

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));
  const hasEmail = form.email.trim().length > 0;
  const membersPath = `/communities/${community.id}/members`;
  const displayName = form.name.trim() || t("new_member.create.new_member");

  const onName = (e) => {
    set({ name: e.target.value });
    if (nameErr && e.target.value.trim()) setNameErr(false);
  };
  // Dashboard access requires an email; clearing the email revokes it.
  const onEmail = (e) =>
    set({
      email: e.target.value,
      has_dashboard_access: e.target.value.trim() ? form.has_dashboard_access : false,
    });

  const roles = [
    {
      key: "is_host",
      icon: "solar_power",
      label: t("new_member.create.role_host"),
      desc: t("new_member.create.role_host_desc"),
    },
    {
      key: "is_promoter",
      icon: "campaign",
      label: t("new_member.create.role_promoter"),
      desc: t("new_member.create.role_promoter_desc"),
    },
    {
      key: "has_dashboard_access",
      icon: "dashboard",
      label: t("new_member.create.role_access"),
      desc: t("new_member.create.role_access_desc"),
      needsEmail: true,
    },
  ];

  const onCreate = async () => {
    if (submitting) return;
    if (!form.name.trim()) {
      setNameErr(true);
      return;
    }
    setSubmitting(true);
    try {
      const member = {
        email: form.email || null,
        name: form.name.trim(),
        phone: form.phone,
        address: form.address,
        nif: form.nif,
        role: "MEMBER",
        status: form.status,
        has_dashboard_access: form.has_dashboard_access,
        is_host: form.is_host,
        is_promoter: form.is_promoter,
      };
      await addNewMember({ member, communityId: community.id }).unwrap();
      navigate(membersPath);
      const notification =
        community.type === "PROD"
          ? t("new_member.notifications.prod", { email: form.email })
          : t("new_member.notifications.demo");
      dispatch(openNotificationPopup({ notification, type: "success" }));
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
      setSubmitting(false);
    }
  };

  const textField = (key, label, placeholder, type = "text") => (
    <div className="rec-field">
      <span className="text-[12.5px] font-semibold text-ink-2">{label}</span>
      <input
        className="rec-input"
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </div>
  );

  return (
    <div>
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: t("new_member.create.members"), to: membersPath },
          { label: t("new_member.create.new_member") },
        ]}
      />

      <div className="rec-page-head">
        <Button variant="ghost" size="sm" onClick={() => navigate(membersPath)}>
          <Icon name="arrow_back" />
          {t("new_member.create.back")}
        </Button>
      </div>

      <div className="rec-wiz-wrap">
        {/* Live preview hero */}
        <div className="rec-card rec-card-pad flex items-center gap-[14px] flex-wrap">
          <div
            className="flex items-center justify-center rounded-xl shrink-0 text-white font-bold"
            style={{ width: 52, height: 52, fontSize: 18, background: avColor(displayName) }}
          >
            {initials(displayName)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] font-bold text-ink leading-tight">{displayName}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[12.5px] text-ink-3">
              <span className="inline-flex items-center gap-1">
                <Icon name="mail" style={{ fontSize: 15 }} />
                {form.email.trim() || t("new_member.create.no_email")}
              </span>
              <span className="inline-flex items-center gap-1">
                <Icon name="badge" style={{ fontSize: 15 }} />
                NIF {form.nif.trim() || "—"}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-end">
            <Chip tone={form.status === "ACTIVE" ? "green" : "red"}>
              {form.status === "ACTIVE"
                ? t("new_member.create.status_active")
                : t("new_member.create.status_inactive")}
            </Chip>
            {form.is_host && <Chip tone="teal">{t("new_member.create.role_host")}</Chip>}
            {form.is_promoter && <Chip tone="amber">{t("new_member.create.role_promoter")}</Chip>}
          </div>
        </div>

        {/* Form */}
        <div className="rec-card rec-card-pad">
          <div className="rec-wiz-card-head">
            <h2>{t("new_member.steps.basic_info")}</h2>
            <p>{t("new_member.create.section_sub")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
            <div className="rec-field sm:col-span-2">
              <span className="text-[12.5px] font-semibold text-ink-2">
                {t("new_member.create.name_label")}
              </span>
              <input
                className={`rec-input ${nameErr ? "err" : ""}`}
                value={form.name}
                placeholder={t("new_member.create.name_ph")}
                onChange={onName}
              />
              {nameErr && (
                <small className="rec-field-err">
                  <Icon name="error" style={{ fontSize: 14 }} />
                  {t("new_member.form.company.error_required")}
                </small>
              )}
            </div>

            <div className="rec-field">
              <span className="text-[12.5px] font-semibold text-ink-2">
                {t("new_member.form.email.label")}
              </span>
              <input
                className="rec-input"
                type="email"
                value={form.email}
                placeholder="nome@exemplo.pt"
                onChange={onEmail}
              />
            </div>
            {textField("phone", t("new_member.form.phone.label"), "+351 ...")}
            {textField("nif", t("new_member.form.nif.label"), "000 000 000")}
            {textField(
              "address",
              t("new_member.form.address1.label"),
              t("new_member.create.address_ph")
            )}
          </div>

          <div className="rec-divider my-5" />

          <div className="rec-field mb-5">
            <span className="text-[12.5px] font-semibold text-ink-2">
              {t("single_member.general.labels.status")}
            </span>
            <div>
              <Segmented
                value={form.status}
                onChange={(v) => set({ status: v })}
                options={[
                  { value: "ACTIVE", label: t("new_member.create.status_active") },
                  { value: "INACTIVE", label: t("new_member.create.status_inactive") },
                ]}
              />
            </div>
          </div>

          <span className="text-[12.5px] font-semibold text-ink-2 block mb-[10px]">
            {t("new_member.create.roles_title")}
          </span>
          <div className="rec-type-pick">
            {roles.map((r) => {
              const on = form[r.key];
              const disabled = r.needsEmail && !hasEmail;
              return (
                <button
                  type="button"
                  key={r.key}
                  disabled={disabled}
                  className={`rec-type-opt ${on ? "on" : ""}`}
                  style={{
                    alignItems: "flex-start",
                    opacity: disabled ? 0.55 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                  onClick={() => set({ [r.key]: !on })}
                >
                  <span
                    className="flex items-center justify-center rounded-[10px] shrink-0"
                    style={{
                      width: 34,
                      height: 34,
                      background: on ? "var(--accent-soft)" : "var(--surface-3)",
                      color: on ? "var(--accent-deep)" : "var(--ink-3)",
                    }}
                  >
                    <Icon name={r.icon} fill style={{ fontSize: 19 }} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-semibold text-ink">{r.label}</span>
                    <span className="block text-[11.5px] font-normal text-ink-3 mt-0.5">
                      {disabled ? t("new_member.form.dashboard_access.no_email_hint") : r.desc}
                    </span>
                  </span>
                  <Icon
                    name={on ? "check_circle" : "radio_button_unchecked"}
                    fill={on}
                    style={{ color: on ? "var(--accent)" : "var(--ink-3)" }}
                  />
                </button>
              );
            })}
          </div>

          <div className="rec-wiz-foot">
            <Button variant="ghost" onClick={() => navigate(membersPath)}>
              {t("cancel", { ns: "common" })}
            </Button>
            <Button disabled={submitting} onClick={onCreate}>
              <Icon name="check" />
              {t("new_member.create.submit")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewMember;
